import type { ReactNode } from 'react';
import { Fragment, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Bars, MiniBars } from '@/components/charts';
import {
  Card,
  Disabled,
  Empty,
  ErrorCard,
  Loaded,
  LOGIN_NEEDED,
  PillTag,
  Seg,
  Select,
  SkeletonCard,
} from '@/components/ui';
import { useApi, useMeta } from '@/lib/api';
import { cx } from '@/lib/cx';
import { fmtDateTime, fmtDay, fmtInt, fmtMoney, fmtPct } from '@/lib/format';
import { feedHref, useParam } from '@/lib/params';
import type { IssueDetail, IssueList, IssueRow, IssueStatus, Pill } from '@/lib/types';

const TABS: { value: IssueStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'new', label: 'New' },
  { value: 'regressed', label: 'Regressed' },
  { value: 'known', label: 'Known' },
  { value: 'ignored', label: 'Ignored' },
  { value: 'resolved', label: 'Resolved' },
];
const STATUS_PILL: Record<IssueStatus, Pill> = {
  open: { tone: 'neu', label: 'Open' },
  new: { tone: 'bad', label: 'New' },
  regressed: { tone: 'warn', label: 'Regressed' },
  known: { tone: 'neu', label: 'Known' },
  ignored: { tone: 'neu', label: 'Ignored' },
  resolved: { tone: 'good', label: 'Resolved' },
};
const DOT: Record<IssueStatus, string> = {
  open: 'var(--line-strong)',
  new: 'var(--bad)',
  regressed: 'var(--warn)',
  known: 'var(--ink-4)',
  ignored: 'var(--line-strong)',
  resolved: 'var(--good)',
};

function IssueLine({
  issue,
  days,
  rangeKey,
  on,
  onClick,
}: {
  issue: IssueRow;
  days: string[];
  rangeKey: string;
  on: boolean;
  onClick: () => void;
}) {
  const sub = [issue.area_label, issue.kind, issue.first_seen_at && `since ${fmtDay(issue.first_seen_at)}`]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className={cx('irow', on && 'on')} onClick={onClick}>
      <span className="dot" style={{ background: DOT[issue.status] }} />
      <div style={{ minWidth: 0 }}>
        <div className="t" title={issue.title}>
          {issue.title}
        </div>
        <div className="s">
          <PillTag pill={STATUS_PILL[issue.status]} />
          {sub}
          {issue.linear_id && <span className="tag">{issue.linear_id}</span>}
        </div>
      </div>
      <MiniBars values={issue.bars} titles={days.map((d, i) => `${fmtDay(d)}: ${issue.bars[i] ?? 0}`)} />
      <div className="n">
        <b>{fmtInt(issue.events)}</b>
        <span>{rangeKey}</span>
      </div>
      <div className="n">
        <b>{fmtInt(issue.companies)}</b>
      </div>
    </div>
  );
}

function Stat({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div>
      <div className="k">{k}</div>
      <div className="v">{v}</div>
    </div>
  );
}

function Detail({ id }: { id: string }) {
  const navigate = useNavigate();
  const query = useApi<IssueDetail>(`/issues/${id}`);
  if (query.error) return <ErrorCard error={query.error} />;
  if (!query.data) return <SkeletonCard height={300} />;
  const d = query.data;
  const i = d.issue;
  const pill = STATUS_PILL[i.status];
  return (
    <>
      <div className="faint" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.05em' }}>
        ISSUE #{i.id} · {i.area_label.toUpperCase()}
      </div>
      <div
        style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-.01em', margin: '4px 0', overflowWrap: 'anywhere' }}
      >
        {i.title}
      </div>
      <div className="muted">
        Grouped by error pattern{i.error_pattern ? `: ${i.error_pattern}` : ''}. AI-written descriptions arrive with AI
        analysis.
      </div>
      {i.status === 'regressed' && (
        <div className="banner" style={{ marginTop: 12, marginBottom: 0 }}>
          ↺ This issue came back after it was resolved.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <span
          className="sel"
          title={LOGIN_NEEDED}
          style={{ color: `var(--${pill.tone === 'neu' ? 'ink-2' : pill.tone})` }}
        >
          ● {pill.label}
        </span>
        <Disabled primary>Create Linear ticket</Disabled>
        <Disabled>Mark known</Disabled>
        <Disabled>Ignore</Disabled>
        <Disabled>Resolve</Disabled>
      </div>
      <div className="stats">
        <Stat
          k={`Events · ${d.window.key}`}
          v={
            <>
              {fmtInt(d.stats.events)}
              {i.status === 'new' && (
                <span className="d dn" style={{ marginLeft: 6 }}>
                  ▲ new
                </span>
              )}
            </>
          }
        />
        <Stat k="Companies" v={fmtInt(d.stats.companies)} />
        <Stat k="Users" v={fmtInt(d.stats.users)} />
        <Stat k="Recovered after rephrasing" v={fmtPct(d.stats.recovered_rate)} />
        <Stat k="Cost of failed asks" v={fmtMoney(d.stats.failed_cost)} />
        <Stat
          k="First / last seen"
          v={`${i.first_seen_at ? fmtDay(i.first_seen_at) : '–'} · ${d.stats.last_at ? fmtDateTime(d.stats.last_at) : '–'}`}
        />
      </div>
      <Card
        title="Events per day"
        right={
          <span className="legend">
            <span>
              <i style={{ background: 'var(--bad)', width: 8, height: 8 }} />
              events
            </span>
          </span>
        }
      >
        <Bars labels={d.bar_days.map(fmtDay)} values={d.per_day} title={(n) => fmtDay(d.bar_days[n])} unit="events" />
      </Card>
      <div className="g2">
        <Card
          title="Sample asks"
          right={
            <span
              className="link"
              onClick={() =>
                navigate(
                  feedHref({
                    area: i.area,
                    outcome: 'not_ok',
                    range: d.window.key === 'custom' ? undefined : d.window.key,
                  }),
                )
              }
            >
              Not-ok asks in {i.area_label} →
            </span>
          }
        >
          {d.samples.length === 0 ? (
            <div className="muted">No events in this range.</div>
          ) : (
            d.samples.map((s) => (
              <div
                key={`${s.source}-${s.source_id}`}
                className={cx('sample', s.ask_id !== null && 'clickable')}
                onClick={s.ask_id !== null ? () => navigate(`/feed?sel=ask::${s.ask_id}&customers=false`) : undefined}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ overflowWrap: 'anywhere' }}>
                    {s.text ? (
                      s.source === 'ask' ? (
                        `“${s.text}”`
                      ) : (
                        s.text
                      )
                    ) : (
                      <span className="faint">(text cleared after 90 days)</span>
                    )}
                  </div>
                  <div className="faint" style={{ fontSize: 12 }}>
                    {[s.company?.name ?? 'No company', s.user, fmtDateTime(s.at)].filter(Boolean).join(' · ')}
                  </div>
                </div>
                {s.recovered === true && <span className="pill warn">recovered</span>}
                {s.recovered === false && <span className="pill bad">gave up</span>}
              </div>
            ))
          )}
        </Card>
        <Card title="Companies affected">
          {d.companies.length === 0 ? (
            <div className="muted">None in this range.</div>
          ) : (
            <table>
              <tbody>
                {d.companies.map((c) => (
                  <tr key={c.company_id} onClick={() => navigate(`/accounts/${c.company_id}`)}>
                    <td>
                      <b>{c.name}</b>
                    </td>
                    <td className="faint">{c.plan ?? '–'}</td>
                    <td className="num">{fmtInt(c.events)}</td>
                  </tr>
                ))}
                {d.companies_more > 0 && (
                  <tr>
                    <td className="muted" colSpan={3}>
                      {d.companies_more} more
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </Card>
      </div>
      <Card
        style={{ marginTop: 12 }}
        title="How events are grouped into this issue"
        right={
          <span className="link disabled" title={LOGIN_NEEDED}>
            Edit grouping
          </span>
        }
      >
        <div className="sig">
          {(
            [
              ['area', d.grouping.area],
              ['step', d.grouping.step],
              ['kind', d.grouping.kind],
              ['error', d.grouping.error],
            ] as const
          ).map(([k, v]) => (
            <Fragment key={k}>
              <span className="k">{k}</span>
              <span style={{ overflowWrap: 'anywhere' }}>{v ?? '–'}</span>
            </Fragment>
          ))}
        </div>
        <div className="rule">
          🔔 Slack alerts for issues arrive with the next sync phase.
          <span className="link disabled" title={LOGIN_NEEDED} style={{ marginLeft: 'auto' }}>
            Change
          </span>
        </div>
      </Card>
      <div className="note">{d.ai_note}</div>
    </>
  );
}

export function IssuesPage() {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const meta = useMeta();
  const [status, setStatus] = useParam('status', 'open');
  const [area, setArea] = useParam('area', '');
  const [sort, setSort] = useParam('sort', 'events');
  const list = useApi<IssueList>('/issues', { status, area, sort });
  const firstId = list.data?.items[0]?.id;
  const settled = Boolean(list.data) && !list.isFetching;
  const inList = list.data?.items.some((i) => String(i.id) === issueId) ?? false;
  const filterKey = `${status}|${area}|${sort}`;
  const settledKey = useRef<string | null>(null);
  useEffect(() => {
    if (!settled) return;
    const changed = settledKey.current !== null && settledKey.current !== filterKey;
    settledKey.current = filterKey;
    if (!issueId) {
      if (firstId !== undefined) navigate(`/issues/${firstId}${location.search}`, { replace: true });
    } else if (changed && !inList) {
      navigate(firstId === undefined ? `/issues${location.search}` : `/issues/${firstId}${location.search}`, {
        replace: true,
      });
    }
  }, [issueId, firstId, settled, inList, filterKey, navigate, location.search]);
  const counts = list.data?.counts;
  return (
    <div className="split">
      <section className="list">
        <div className="lhead">
          <span style={{ fontSize: 18, fontWeight: 700, marginRight: 6 }}>Issues</span>
          <Seg
            compact
            value={(TABS.find((t) => t.value === status)?.value ?? 'open') as IssueStatus}
            onChange={setStatus}
            options={TABS.map((t) => ({ value: t.value, label: counts ? `${t.label} ${counts[t.value]}` : t.label }))}
          />
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Select
              value={area}
              active={Boolean(area)}
              onChange={(v) => setArea(v || null)}
              options={[
                { value: '', label: 'All areas' },
                ...(meta.data?.areas ?? []).map((a) => ({ value: a.key, label: a.label })),
              ]}
            />
            <Select
              value={sort}
              onChange={setSort}
              options={[
                { value: 'events', label: 'Sort: events' },
                { value: 'recent', label: 'Sort: most recent' },
              ]}
            />
          </span>
        </div>
        <div className="colh">
          <span />
          <span>Issue</span>
          <span>Last 14 days</span>
          <span>Events</span>
          <span>Companies</span>
        </div>
        <Loaded
          query={list}
          skeleton={
            <div style={{ padding: 16 }}>
              <SkeletonCard />
            </div>
          }
        >
          {(data) =>
            data.items.length === 0 ? (
              <Empty icon="✓" title={status === 'open' ? 'No open issues' : `No ${status} issues`}>
                {status === 'open'
                  ? `Nothing new or regressed in this range. ${data.counts.known} known issues are in the Known tab.`
                  : 'Nothing in this tab.'}
              </Empty>
            ) : (
              <>
                {data.items.map((i) => (
                  <IssueLine
                    key={i.id}
                    issue={i}
                    days={data.bar_days}
                    rangeKey={data.window.key}
                    on={String(i.id) === issueId}
                    onClick={() => navigate(`/issues/${i.id}${location.search}`)}
                  />
                ))}
                <div className="note" style={{ padding: '8px 16px' }}>
                  {data.ai_note}
                </div>
              </>
            )
          }
        </Loaded>
      </section>
      <section className="detail">
        {issueId ? <Detail key={issueId} id={issueId} /> : <Empty icon="▸" title="Select an issue" />}
      </section>
    </div>
  );
}
